import api from './api';
import type { Group, User } from '@/types/user.types';
import type { ApiResponse } from '@/types/api.types';

export interface CreateGroupRequest {
  name: string;
  description?: string;
}

export interface UpdateGroupRequest {
  name?: string;
  description?: string;
  // Backend UpdateGroupRequest.IsActive *bool — omit to leave unchanged (A-17)
  is_active?: boolean;
}

export interface GroupResourceStats {
  files_count: number;
  backup_jobs_count: number;
  schedules_count: number;
  destinations_count: number;
  watchers_count: number;
}

// What removing a member would carry with them (ADR-047): their own rows in
// this group, and the group those rows land in. to_group_id is absent when
// this is the member's last group — the admin picks the destination then.
export interface MemberCarry {
  files: number;
  watchers: number;
  schedules: number;
  backup_jobs: number;
  to_group_id?: string;
  to_group_name?: string;
}

export interface GroupMember extends User {
  is_primary_group?: boolean;
  carry?: MemberCarry;
}

export interface GroupDetail extends Group {
  members?: GroupMember[];
  resource_stats?: GroupResourceStats;
}

export interface RemoveMemberResult {
  resources_moved_to?: string;
  resources_moved?: Record<string, number>;
}

export interface DeleteGroupResult {
  moved_to?: string;
  moved?: Record<string, number>;
  moved_memberships?: number;
}

export const groupService = {
  async listGroups(): Promise<Group[]> {
    const response = await api.get<ApiResponse<Group[]>>('/api/v1/groups');
    return response.data.data || [];
  },

  async getGroup(id: string): Promise<GroupDetail> {
    const response = await api.get<ApiResponse<GroupDetail>>(`/api/v1/groups/${id}`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to get group');
  },

  async createGroup(data: CreateGroupRequest): Promise<Group> {
    const response = await api.post<ApiResponse<Group>>('/api/v1/groups', data);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to create group');
  },

  async updateGroup(id: string, data: UpdateGroupRequest): Promise<Group> {
    const response = await api.put<ApiResponse<Group>>(`/api/v1/groups/${id}`, data);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to update group');
  },

  // moveTo: the group that receives the resources and deactivated members
  // still pointing at this one (QA #49); without it the backend answers
  // 409 GROUP_DELETE_REQUIRES_DESTINATION, which the page turns into a
  // destination picker rather than a snackbar.
  async deleteGroup(id: string, moveTo?: string): Promise<DeleteGroupResult> {
    const response = await api.delete<ApiResponse<DeleteGroupResult>>(`/api/v1/groups/${id}`, {
      params: moveTo ? { move_to: moveTo } : undefined,
      skipGlobalError: true,
    });
    if (!response.data.success) {
      throw new Error('Failed to delete group');
    }
    return response.data.data ?? {};
  },

  async addMember(groupId: string, userId: string, setAsPrimary?: boolean): Promise<void> {
    const response = await api.post<ApiResponse>(`/api/v1/groups/${groupId}/members`, {
      user_id: userId,
      set_as_primary: setAsPrimary,
    });
    if (!response.data.success) {
      throw new Error('Failed to add member');
    }
  },

  // moveTo: the group the user joins when this is their last one (QA #40);
  // without it the backend answers 409 LAST_GROUP_REQUIRES_DESTINATION,
  // which the page turns into a destination picker rather than a snackbar.
  async removeMember(groupId: string, userId: string, moveTo?: string): Promise<RemoveMemberResult> {
    const response = await api.delete<ApiResponse<RemoveMemberResult>>(`/api/v1/groups/${groupId}/members/${userId}`, {
      params: moveTo ? { move_to: moveTo } : undefined,
      skipGlobalError: true,
    });
    if (!response.data.success) {
      throw new Error('Failed to remove member');
    }
    return response.data.data ?? {};
  },
};
